'use client'

/**
 * The `/build` client island.
 *
 * It holds the selection state, derives the running total from it, and hands
 * that exact number to the cart. Presentation lives in the sibling components;
 * arithmetic lives only here, so the price on screen and the price charged are
 * the same value rather than two agreeing calculations.
 *
 * It is the ONLY client component on the route. Everything else — the model
 * strap, imagery, metadata — renders on the server, so keep heavy imports out
 * of here. In particular never import the `@sifly/shared-ui` ROOT barrel from
 * this file: a single such import in a client module once put 357 KB on every
 * page. Subpaths only.
 *
 * Nothing here reaches `_lib/loadBuild`, by module layout rather than by
 * discipline: that file holds the Supabase loader, so `BuildSlot` and
 * `resolveInitialSelection` live in the pure `_lib/buildSlot` instead and
 * `encodeBuild`'s module imports the default selection from there. A value
 * edge into the loader would put the server client in this bundle.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import type { BundleDiscountRule } from '@sifly/shared-utils'
import { computeBundleDeltaWithDiscounts, resolveBundleSelections } from '@sifly/shared-ui/bundle'
import type { BundleSelection } from '@sifly/shared-ui/bundle'
import { useCart } from '@/lib/cart'
import { trackMetaEvent } from '@/lib/metaPixel'
import { initAnalytics, track } from '@/lib/analytics'
import type { BuildSlot } from '../_lib/buildSlot'
import { encodeBuild } from '../_lib/buildToken'
import { money } from '../_lib/money'
import { OptionCard } from './OptionCard'
import { BuildSummary } from './BuildSummary'

// The rendered "None" card label, also reported to analytics when it's
// picked (`pick`'s explicit-null path — distinct from `toggle`'s lowercase
// 'none' sentinel, which is not a rendered label). One constant for all
// three sites so a copy change can't make analytics report a label no
// customer ever saw.
const NONE_LABEL = 'None'

export type BuildConfiguratorProps = {
  productId: string
  productSlug: string
  productName: string
  basePrice: number
  photo: string | null
  slots: BuildSlot[]
  discountRules: BundleDiscountRule[]
  initialSelection: BundleSelection
  tenantId: string
  /** Set only when the visitor arrived via the quiz's "build one" hand-off
   *  (Task 9). `?from=` is visitor-supplied, so this is allowlisted to the one
   *  literal value at the page level — never passed through raw. */
  from?: 'quiz'
}

export function BuildConfigurator({
  productId,
  productSlug,
  productName,
  basePrice,
  photo,
  slots,
  discountRules,
  initialSelection,
  tenantId,
  from,
}: BuildConfiguratorProps) {
  const [selection, setSelection] = useState<BundleSelection>(initialSelection)
  const [added, setAdded] = useState(false)
  const cart = useCart()

  // Has the CUSTOMER changed anything? Only their edits may rewrite the address
  // bar — see the URL sync below.
  const [edited, setEdited] = useState(false)

  // The model strap navigates with <Link>, which re-renders this island in
  // place rather than remounting it — so without this the selection map from
  // the PREVIOUS model survives, keyed by slot ids the new model doesn't have.
  // Every slot would read as unanswered and Add to cart would sit disabled.
  // (React's documented "adjust state when a prop changes" pattern; it costs
  // one extra render only on the render where the model actually changed.)
  //
  // `edited` resets with it: the strap is a navigation, not a configuration
  // change, and the server has just rendered the correct URL for the model
  // being shown. Leaving it set would rewrite that URL with a token of the new
  // model's untouched defaults.
  const [selectionFor, setSelectionFor] = useState(productId)
  if (selectionFor !== productId) {
    setSelectionFor(productId)
    setSelection(initialSelection)
    setEdited(false)
  }

  // Usage reporting: one `build_view` per mount, one `build_model_select` per
  // strap switch thereafter. This can NOT live in the render-phase adjustment
  // above — that runs during render, and `track` is a side effect. Instead: a
  // ref holding the previous slug, in an effect keyed on `productSlug`,
  // initialised to `productSlug` so the first run (which also fires on mount)
  // sees no change and skips straight to the mount-only branch below rather
  // than reporting a `from === to` select.
  const prevSlugRef = useRef(productSlug)
  const mountedRef = useRef(false)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      // On a direct load (a pasted share link — the exact case Task 7 exists
      // for) this effect can run before AnalyticsScripts' own init effect.
      // The consent verdict doesn't depend on that order any more, but
      // initAnalytics also registers the flush-on-hide listener. Idempotent.
      // Mirrors ProductViewBeacon.tsx.
      initAnalytics(tenantId)
      trackMetaEvent('ViewContent', { content_ids: [productId], content_type: 'product', value: basePrice, currency: 'USD' })
      track('build_view', { model: productSlug, ...(from === 'quiz' ? { from } : {}) })
    } else if (prevSlugRef.current !== productSlug) {
      trackMetaEvent('ViewContent', { content_ids: [productId], content_type: 'product', value: basePrice, currency: 'USD' })
      track('build_model_select', { from: prevSlugRef.current, to: productSlug })
    }
    prevSlugRef.current = productSlug
  }, [productSlug, productId, basePrice, tenantId, from])

  // The address bar IS the share link, so mirror the live selection into
  // `?set=<slug>&opts=<token>` on a ~250 ms debounce.
  //
  // Gated on `edited`, so arriving at the page never moves the URL. A visitor
  // who lands on /build and touches nothing gets no rewrite; nor does a
  // recipient who opens a shared link and just reads it. That second case is
  // why the gate is a flag and not "the token differs from the one in the URL":
  // any degradation on the way in — a truncated pair, an option since deleted,
  // a slot added after the link was made — decodes to a selection whose token
  // is legitimately a different STRING from the one the sender sent, so a
  // difference test would rewrite a link nobody touched, silently overwriting
  // the sender's token with the recipient's downgraded one.
  //
  // `replaceState`, never `pushState`: every option click would otherwise add a
  // history entry, and Back would walk the customer backwards through their own
  // configuration one radio at a time instead of leaving the page.
  //
  // The dependency is the TOKEN, not `slots`/`selection`. A re-render that hands
  // this island a fresh `slots` array describing the same catalog must not
  // rewrite the URL — depending on object identity risks a write-triggers-render
  // -triggers-write loop, and a string cannot start one.
  //
  // `set` is WRITTEN, from the model that actually rendered — not preserved
  // from the current URL. A link made on bare `/build` carries no `set`, so it
  // decodes against whatever `sort_order` makes the default at OPEN time:
  // reorder the eFoil sets and every such link quietly resolves against a
  // different board than the sender was looking at, its option ids failing to
  // match and reverting to that other board's defaults. Pinning the slug is the
  // only thing that makes a share link mean one configuration forever.
  const token = useMemo(() => encodeBuild(slots, selection), [slots, selection])
  useEffect(() => {
    if (!edited) return
    // Cleared on unmount too: a write landing after navigation would mutate a
    // URL that no longer belongs to this page.
    const t = setTimeout(() => {
      const params = new URLSearchParams(window.location.search)
      params.set('set', productSlug)
      // A visitor who arrived via the quiz carries `from=quiz` in the address
      // bar. Preserving it here would hand that attribution to whoever they
      // share the link with next — a recipient who never took the quiz would
      // have their `build_view` misattributed to it. Only the visit that
      // actually followed the quiz hand-off owns that tag.
      params.delete('from')
      // `opts` is appended by hand rather than set on `params`. URLSearchParams
      // percent-encodes the pair separator, and a wall of `%2C` in a link a
      // human is about to paste reads as corruption — but undoing that with a
      // global `.replace(/%2C/g, ',')` over the whole query string also decodes
      // commas inside params this island does not own (`?utm_content=a%2Cb`).
      // The token is `[0-9a-f]`, `-`, `,` and `x` only, all legal unencoded in
      // a query (RFC 3986), so appending it raw needs no encoding and touches
      // nobody else's value.
      params.delete('opts')
      const qs = `${params.toString()}&opts=${token}`
      // Path and hash are restated explicitly: a bare `?…` resolves to
      // path+query, which drops any `#fragment` the visitor arrived with.
      window.history.replaceState(null, '', `${window.location.pathname}?${qs}${window.location.hash}`)
    }, 250)
    return () => clearTimeout(t)
  }, [token, edited, productSlug])

  // A slot with no options is not a question. Rendering one produced an empty
  // section the customer could stare at but never act on — and, when the slot
  // was also required, permanently disabled Add to Cart on a live product.
  // Hence the `.length > 0` here AND in `ready` below; the shared
  // `isBundleSelectionComplete` does not guard it on this branch.
  const steps = useMemo(() => slots.filter(s => s.presentation === 'step' && s.options.length > 0), [slots])
  const addons = useMemo(() => slots.filter(s => s.presentation === 'addon' && s.options.length > 0), [slots])
  const included = useMemo(() => slots.filter(s => s.presentation === 'included' && s.options.length > 0), [slots])

  // `delta` is ALREADY net of `discount` — that is the helper's contract. The
  // total is basePrice + delta; `discount`/`applied` come back purely so the
  // saving can be SHOWN. Subtracting it again here would be invisible on a
  // build with no rules and would understate every build with one.
  const { delta, applied } = useMemo(
    () => computeBundleDeltaWithDiscounts(slots, selection, discountRules),
    [slots, selection, discountRules],
  )
  const total = basePrice + delta

  const ready = useMemo(
    () => slots.every(s => !s.is_required || s.options.length === 0 || !!selection[s.id]),
    [slots, selection],
  )

  const resolved = useMemo(() => resolveBundleSelections(slots, selection), [slots, selection])

  // What each slot actually resolved to, by slot id. The manifest reads from
  // this rather than from `options[0]`: `buildDefaultSelection` picks
  // `is_default` first and only falls back to the first option, so a two-option
  // included slot with `is_default` on the second would have named the wrong
  // part while charging for the right one.
  const chosenLabel = useMemo(
    () => new Map(resolved.map(({ slot, option }) => [slot.id, option.label])),
    [resolved],
  )

  // An optional `included` slot has no OptionCard — nothing on the page can
  // ever select it — and `resolveInitialSelection` forces it to null, so it
  // never resolves and is never charged. Filtering the manifest down to
  // slots that actually resolved keeps it from listing something the
  // customer isn't getting.
  const includedResolved = useMemo(
    () => included.filter(slot => chosenLabel.has(slot.id)),
    [included, chosenLabel],
  )

  // Both mark the configuration as edited. That flag is what licenses the URL
  // sync above to write, so it must be set by every path that changes the
  // selection — and only by those paths.
  //
  // Both also report `build_option_select`. The resulting option id is
  // computed here from `selection` (this render's closure, i.e. the value
  // BEFORE this click) rather than read back out of the `setSelection`
  // updater — that updater runs during React's own reconciliation, not
  // synchronously at the call site, so a value read from inside it here would
  // still be stale.
  function pick(slotId: string, optionId: string | null) {
    setEdited(true)
    setSelection(prev => ({ ...prev, [slotId]: optionId }))
    reportOptionSelect(slotId, optionId, NONE_LABEL)
  }

  function toggle(slotId: string, optionId: string) {
    setEdited(true)
    const next = selection[slotId] === optionId ? null : optionId
    setSelection(prev => ({ ...prev, [slotId]: prev[slotId] === optionId ? null : optionId }))
    reportOptionSelect(slotId, next, 'none')
  }

  // `pick`'s explicit null comes from clicking a rendered "None" card, so
  // `noneLabel` there is the literal "None" the customer saw and clicked.
  // `toggle`'s null is different: a single-option add-on is a checkbox, not a
  // radio group, so there is no "None" card to report — lowercase 'none'
  // distinguishes "the customer cleared this" from "the customer picked the
  // card labelled None" in the analytics stream.
  function reportOptionSelect(slotId: string, optionId: string | null, noneLabel: string) {
    const slot = slots.find(s => s.id === slotId)
    if (!slot) return
    const label = optionId === null ? noneLabel : (slot.options.find(o => o.id === optionId)?.label ?? optionId)
    track('build_option_select', { model: productSlug, slot: slot.label, option: label })
  }

  // Same construction as the PDP's bundle branch, so a board configured here
  // and one configured there are the same cart line. `unitPrice` is the exact
  // figure the summary displays.
  const addedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (addedTimer.current) clearTimeout(addedTimer.current) }, [])

  function handleAdd() {
    const bundleOptions = resolved.map(({ slot, option }) => ({ label: slot.label, value: option.label }))
    const description = bundleOptions.map(({ label, value }) => `${label}: ${value}`).join(' · ')
    cart.addItem({
      // Fixed: /build renders the SiFly catalog only.
      source: 'sifly',
      productId,
      productName,
      productType: 'bundle',
      skuId: null,
      skuName: description.slice(0, 60) || 'Bundle',
      quantity: 1,
      unitPrice: total,
      photo,
      bundleOptionIds: resolved.map(r => r.option.id),
      bundleDescription: description,
      bundleOptions,
    })
    // `total`/`optionCount` are numbers on screen but track()'s metadata type
    // is Record<string, string> and the server drops any non-string value in
    // silence — String() them here or this is the one figure the event exists
    // to record disappearing without a trace.
    //
    // Key is `total_cents`, not `total`: `total` here is DOLLARS (this route's
    // one exception — see _lib/money.ts), while every other money value in the
    // repo (Stripe amounts, formatCents, order/quote/booking columns) is
    // integer cents. A bare `total` key reads as cents to any analyst joining
    // against revenue and is off by 100x with nothing failing. The key name
    // carries the unit; a comment on this line wouldn't survive into the event
    // stream.
    track('build_add_to_cart', {
      model: productSlug,
      total_cents: String(Math.round(total * 100)),
      optionCount: String(resolved.length),
    })
    setAdded(true)
    if (addedTimer.current) clearTimeout(addedTimer.current)
    addedTimer.current = setTimeout(() => setAdded(false), 2000)
  }

  // Mobile sticky bar: the summary is a desktop sidebar, so on a phone the
  // price and the action scroll away entirely. Reveal a fixed bar once the
  // summary leaves the viewport. jsdom has no IntersectionObserver and an
  // unguarded constructor at mount would crash every render test.
  const summaryRef = useRef<HTMLDivElement | null>(null)
  const [summaryInView, setSummaryInView] = useState(true)
  useEffect(() => {
    const el = summaryRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const obs = new IntersectionObserver(([entry]) => setSummaryInView(entry.isIntersecting))
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const summary = (
    <BuildSummary
      basePrice={basePrice}
      // EVERY selected option, included slots among them. The delta helper sums
      // all of them, so filtering any out here would leave the column summing to
      // the total only by the coincidence that included options are $0 today —
      // and migration 367 exists precisely so a merchandiser can mark a PAID
      // slot `included`. That would print a total with money in it that no row
      // accounts for, on the one screen whose job is explaining the price.
      // `signed(0)` already renders 'Included' for the ordinary $0 case.
      lines={resolved.map(({ slot, option }) => ({
        id: slot.id,
        slot: slot.label,
        option: option.label,
        delta: option.price_delta,
      }))}
      savings={applied.map(rule => ({ id: rule.id, label: rule.label, amount: rule.discount_amount }))}
      total={total}
      ready={ready}
      added={added}
      onAdd={handleAdd}
    />
  )

  return (
    <>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
        <div className="space-y-8">
          {steps.map((slot, i) => (
            <fieldset key={slot.id}>
              {/* The h2 is load-bearing, not decoration. Everything else on the
                  page that names a region — "Enhance your ride", "What's in the
                  box", "Your build" — is an h2, so a bare legend made the
                  configuration steps the ONE thing heading navigation skips:
                  press H down /build and you hear every aside and never the
                  task. `legend`'s content model admits heading content, and
                  Tailwind's preflight resets heading font-size/weight/margin to
                  inherit, so the rendered result is byte-identical. */}
              <legend className="text-sm font-medium text-[var(--color-text-primary)]">
                <h2>
                  <span className="text-[var(--color-text-secondary)]">{i + 1}. </span>
                  {slot.label}
                </h2>
              </legend>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {/* An optional step is still a step: without an explicit way
                    back to "none", picking one radio is a one-way door. */}
                {!slot.is_required && (
                  <OptionCard
                    control="radio"
                    name={slot.id}
                    label={NONE_LABEL}
                    priceDelta={0}
                    selected={!selection[slot.id]}
                    onSelect={() => pick(slot.id, null)}
                  />
                )}
                {slot.options.map(opt => (
                  <OptionCard
                    key={opt.id}
                    control="radio"
                    name={slot.id}
                    label={opt.label}
                    priceDelta={opt.price_delta}
                    selected={selection[slot.id] === opt.id}
                    onSelect={() => pick(slot.id, opt.id)}
                  />
                ))}
              </div>
            </fieldset>
          ))}

          {addons.length > 0 && (
            <section>
              <h2 className="text-sm font-medium text-[var(--color-text-primary)]">Enhance your ride</h2>
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                Optional — some pair with your board for a combo saving.
              </p>
              <div className="mt-3 space-y-4">
                {/* `selection` holds ONE option id per slot, so the options of
                    an add-on slot are mutually exclusive whether or not the
                    control admits it. A checkbox says otherwise: on a
                    two-option add-on, ticking the second silently unticks the
                    first — moving the price by an amount the customer never
                    asked for, while a screen reader announces "checked" on a
                    control that then quietly unchecks itself. So a checkbox
                    only where it is TRUE, on a one-option yes/no; anything
                    wider gets radios and the same explicit None a step uses.
                    Every add-on has one option in production today, which is
                    exactly why this had to be fixed before it had two. */}
                {addons.map(slot => (
                  <fieldset key={slot.id}>
                    {/* One option: the checkbox's own label ("Add Boardbag")
                        already carries the whole meaning, so the legend stays
                        sr-only. Two or more: the "None" card's label carries
                        zero information on its own ("none of what?"), so the
                        legend must be visible — same classes as a step
                        legend, deliberately without the numbering or the h2,
                        since add-ons are un-numbered and already sit under
                        the "Enhance your ride" h2. */}
                    <legend
                      className={
                        slot.options.length === 1
                          ? 'sr-only'
                          : 'text-sm font-medium text-[var(--color-text-primary)]'
                      }
                    >
                      {slot.label}
                    </legend>
                    <div className={slot.options.length === 1 ? 'grid gap-2 sm:grid-cols-2' : 'mt-3 grid gap-2 sm:grid-cols-2'}>
                      {slot.options.length === 1 ? (
                        <OptionCard
                          key={slot.options[0].id}
                          control="checkbox"
                          name={slot.id}
                          label={slot.options[0].label}
                          priceDelta={slot.options[0].price_delta}
                          selected={selection[slot.id] === slot.options[0].id}
                          onSelect={() => toggle(slot.id, slot.options[0].id)}
                        />
                      ) : (
                        <>
                          {!slot.is_required && (
                            <OptionCard
                              control="radio"
                              name={slot.id}
                              label={NONE_LABEL}
                              priceDelta={0}
                              selected={!selection[slot.id]}
                              onSelect={() => pick(slot.id, null)}
                            />
                          )}
                          {slot.options.map(opt => (
                            <OptionCard
                              key={opt.id}
                              control="radio"
                              name={slot.id}
                              label={opt.label}
                              priceDelta={opt.price_delta}
                              selected={selection[slot.id] === opt.id}
                              onSelect={() => pick(slot.id, opt.id)}
                            />
                          ))}
                        </>
                      )}
                    </div>
                  </fieldset>
                ))}
              </div>
            </section>
          )}

          {/* "What's in the box" — a manifest, not a question. Deliberately
              inline rather than its own component: a dozen lines of <ul>/<li>
              with one call site and zero interactive semantics is clearer here
              than behind an import. */}
          {includedResolved.length > 0 && (
            <section className="rounded-[var(--brand-radius,0.5rem)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-4">
              <h2 className="text-sm font-medium text-[var(--color-text-primary)]">What&rsquo;s in the box</h2>
              <ul className="mt-2 space-y-1 text-sm text-[var(--color-text-secondary)]">
                {includedResolved.map(slot => (
                  <li key={slot.id}>{chosenLabel.get(slot.id)}</li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div ref={summaryRef} className="lg:sticky lg:top-24">
          {summary}
        </div>
      </div>

      {!summaryInView && (
        <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-4 border-t border-[var(--color-border)] bg-[var(--color-bg-primary)] px-4 py-3 lg:hidden">
          <div>
            <p className="text-xs text-[var(--color-text-secondary)]">{productName}</p>
            <p className="text-base font-semibold tabular-nums text-[var(--color-text-primary)]">
              {money(total)}
            </p>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            disabled={!ready}
            aria-disabled={!ready}
            className="cta-primary shrink-0 rounded-[var(--layout-button-radius,var(--brand-radius,0.5rem))] px-5 py-2.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {added ? 'Added!' : 'Add to cart'}
          </button>
        </div>
      )}
    </>
  )
}
